import { Type, type Static, type TProperties } from "typebox";
import { Check } from "typebox/value";

export const PROTOCOL_VERSION = 4 as const;
export const MIN_CLIENT_PROTOCOL_VERSION = 4 as const;
export const MIN_NODE_PROTOCOL_VERSION = 3 as const;
export const MIN_PROBE_PROTOCOL_VERSION = 3 as const;
export const MAX_PAYLOAD_BYTES = 25 * 1024 * 1024;
export const MAX_BUFFERED_BYTES = 50 * 1024 * 1024;
export const TICK_INTERVAL_MS = 15_000;

const NonEmptyString = Type.String({ minLength: 1 });
const closed = <T extends TProperties>(properties: T) =>
  Type.Object(properties, { additionalProperties: false });

export const GatewayClientIds = [
  "webchat-ui",
  "openclaw-control-ui",
  "openclaw-tui",
  "webchat",
  "cli",
  "gateway-client",
  "openclaw-macos",
  "openclaw-ios",
  "openclaw-watchos",
  "openclaw-android",
  "node-host",
  "openclaw-worker",
  "test",
  "fingerprint",
  "openclaw-probe",
] as const;

export type GatewayClientId = (typeof GatewayClientIds)[number];
export type GatewayClientMode = "webchat" | "cli" | "ui" | "backend" | "node" | "worker" | "probe" | "test";

export const ErrorShapeSchema = closed({
  code: NonEmptyString,
  message: NonEmptyString,
  details: Type.Optional(Type.Unknown()),
  retryable: Type.Optional(Type.Boolean()),
  retryAfterMs: Type.Optional(Type.Integer({ minimum: 0 })),
});

export const RequestFrameSchema = closed({
  type: Type.Literal("req"),
  id: NonEmptyString,
  method: NonEmptyString,
  params: Type.Optional(Type.Unknown()),
});

export const ResponseFrameSchema = closed({
  type: Type.Literal("res"),
  id: NonEmptyString,
  ok: Type.Boolean(),
  payload: Type.Optional(Type.Unknown()),
  error: Type.Optional(ErrorShapeSchema),
});

export const EventFrameSchema = closed({
  type: Type.Literal("event"),
  event: NonEmptyString,
  payload: Type.Optional(Type.Unknown()),
  seq: Type.Optional(Type.Integer({ minimum: 0 })),
  stateVersion: Type.Optional(Type.Integer({ minimum: 0 })),
});

export const ConnectParamsSchema = closed({
  minProtocol: Type.Integer({ minimum: 1 }),
  maxProtocol: Type.Integer({ minimum: 1 }),
  client: closed({
    id: NonEmptyString,
    displayName: Type.Optional(NonEmptyString),
    version: NonEmptyString,
    platform: NonEmptyString,
    mode: NonEmptyString,
    instanceId: Type.Optional(NonEmptyString),
  }),
  caps: Type.Optional(Type.Array(NonEmptyString)),
  commands: Type.Optional(Type.Array(NonEmptyString)),
  permissions: Type.Optional(Type.Record(NonEmptyString, Type.Boolean())),
  pathEnv: Type.Optional(Type.String()),
  role: Type.Optional(NonEmptyString),
  scopes: Type.Optional(Type.Array(NonEmptyString)),
  device: Type.Optional(
    closed({
      id: NonEmptyString,
      publicKey: NonEmptyString,
      signature: NonEmptyString,
      signedAt: Type.Integer({ minimum: 0 }),
      nonce: NonEmptyString,
    }),
  ),
  auth: Type.Optional(
    closed({
      token: Type.Optional(Type.String()),
      bootstrapToken: Type.Optional(Type.String()),
      deviceToken: Type.Optional(Type.String()),
      password: Type.Optional(Type.String()),
      approvalRuntimeToken: Type.Optional(Type.String()),
      agentRuntimeIdentityToken: Type.Optional(Type.String()),
    }),
  ),
  locale: Type.Optional(Type.String()),
  userAgent: Type.Optional(Type.String()),
});

export type ErrorShape = Static<typeof ErrorShapeSchema>;
export type RequestFrame = Static<typeof RequestFrameSchema>;
export type ResponseFrame = Static<typeof ResponseFrameSchema>;
export type EventFrame = Static<typeof EventFrameSchema>;
export type ConnectParams = Static<typeof ConnectParamsSchema>;

export type HelloOk = {
  type: "hello-ok";
  protocol: number;
  server: { version: string; connId: string };
  features: { methods: string[]; events: string[]; capabilities?: string[] };
  snapshot: Record<string, unknown>;
  auth: { deviceToken?: string; role: string; scopes: string[]; issuedAtMs?: number };
  policy: { maxPayload: number; maxBufferedBytes: number; tickIntervalMs: number };
};

export type GatewayFrame = RequestFrame | ResponseFrame | EventFrame;

export const CORE_METHODS = [
  "connect",
  "health",
  "status",
  "agent",
  "agent.wait",
  "chat.send",
  "chat.history",
  "chat.abort",
  "sessions.list",
  "sessions.preview",
  "sessions.describe",
  "sessions.resolve",
  "sessions.get",
  "sessions.patch",
  "sessions.reset",
  "sessions.delete",
  "sessions.compact",
  "tools.catalog",
  "tools.effective",
  "tools.invoke",
  "skills.status",
  "skills.skillCard",
  "skills.search",
  "skills.detail",
  "agents.list",
  "agents.create",
  "agents.update",
  "agents.delete",
  "models.list",
  "models.probe",
  "models.auth.status",
  "models.auth.logout",
  "memory.status",
  "memory.search",
  "memory.reindex",
  "mcp.status",
  "mcp.reload",
  "device.pair.list",
  "device.pair.approve",
  "device.pair.reject",
  "device.pair.remove",
  "device.token.rotate",
  "device.token.revoke",
  "node.list",
  "node.describe",
  "node.invoke",
  "node.invoke.result",
  "node.pair.list",
  "node.pair.approve",
  "node.pair.reject",
  "node.pair.remove",
  "node.rename",
  "cron.add",
  "cron.get",
  "cron.list",
  "cron.update",
  "cron.remove",
  "cron.run",
  "cron.runs",
  "cron.status",
  "tasks.list",
  "tasks.get",
  "tasks.cancel",
  "channels.status",
  "channels.probe",
  "channels.start",
  "channels.stop",
  "channels.logout",
  "message.send",
  "config.get",
  "config.set",
  "config.patch",
  "logs.tail",
  "pairing.list",
  "pairing.approve",
  "plugins.list",
  "approvals.list",
  "approvals.resolve",
] as const;

export const CORE_EVENTS = [
  "connect.challenge",
  "tick",
  "shutdown",
  "agent",
  "chat",
  "sessions.changed",
  "exec.approval.requested",
  "exec.approval.resolved",
  "channel.status",
  "cron",
  "task",
  "hook",
] as const;

export class GatewayProtocolError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly details?: unknown,
    readonly retryable = false,
  ) {
    super(message);
    this.name = "GatewayProtocolError";
  }
}

export function isRequestFrame(value: unknown): value is RequestFrame {
  return Check(RequestFrameSchema, value);
}

export function isConnectParams(value: unknown): value is ConnectParams {
  return Check(ConnectParamsSchema, value);
}

export function success(id: string, payload?: unknown): ResponseFrame {
  return payload === undefined ? { type: "res", id, ok: true } : { type: "res", id, ok: true, payload };
}

export function failure(id: string, error: ErrorShape): ResponseFrame {
  return { type: "res", id, ok: false, error };
}
